import { toast } from "sonner";
import { appAlert, appConfirm, appPrompt, appDismissDialog } from "../context/AppDialogContext";

function stripHtml(value) {
  if (value == null) return "";
  const str = String(value);
  if (!/[<>]/.test(str)) return str;
  return str
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\s+\n/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

function messageFromOptions(options, argText, argIcon) {
  if (typeof options === "string") {
    return { title: options, message: argText || "", icon: argIcon || "info" };
  }
  return {
    title: options?.title || "",
    message: stripHtml(options?.text || options?.html || options?.message || ""),
    icon: options?.icon || "info",
  };
}

function mapVariant(icon) {
  if (icon === "success") return "success";
  if (icon === "error") return "error";
  if (icon === "warning") return "warning";
  return "info";
}

/** Non-blocking toast — preferred for save success / errors */
export function notifySuccess(message, title) {
  toast.success(title ? `${title}: ${message}` : message);
}

export function notifyError(message, title) {
  toast.error(title ? `${title}: ${message}` : message);
}

export function notifyWarning(message, title) {
  toast.warning(title ? `${title}: ${message}` : message);
}

export function notifyInfo(message, title) {
  toast.message(title ? `${title}: ${message}` : message);
}

export async function notifyAlert(options) {
  const { title, message, icon } = messageFromOptions(options);
  await appAlert({
    title: title || "Notice",
    message,
    variant: mapVariant(icon),
    confirmText: options?.confirmButtonText || "OK",
  });
  return { isConfirmed: true, value: undefined };
}

export async function notifyConfirm(options) {
  const { title, message, icon } = messageFromOptions(options);
  const isConfirmed = await appConfirm({
    title: title || "Please confirm",
    message,
    variant: mapVariant(icon),
    confirmText: options?.confirmButtonText || "Confirm",
    cancelText: options?.cancelButtonText || "Cancel",
  });
  return { isConfirmed, value: undefined };
}

export async function notifyPrompt(options) {
  const { title, message, icon } = messageFromOptions(options);
  const result = await appPrompt({
    title: title || "Enter a value",
    message,
    variant: mapVariant(icon),
    input: options?.input || "text",
    inputPlaceholder: options?.inputPlaceholder || "",
    inputValue: options?.inputValue || "",
    confirmText: options?.confirmButtonText || "OK",
    cancelText: options?.cancelButtonText || "Cancel",
    inputValidator: options?.inputValidator,
  });
  return result;
}

/**
 * Drop-in replacement for Swal.fire — uses toasts for success/error,
 * AppDialog for confirm/prompt, toast for timed messages.
 */
export async function swalFire(options, text, icon) {
  if (typeof options === "string") {
    const mapped = messageFromOptions(options, text, icon);
    if (mapped.icon === "success") {
      notifySuccess(mapped.message || mapped.title, mapped.message ? mapped.title : "");
      return { isConfirmed: true };
    }
    if (mapped.icon === "error") {
      notifyError(mapped.message || mapped.title, mapped.message ? mapped.title : "");
      return { isConfirmed: true };
    }
    await notifyAlert({ title: mapped.title, text: mapped.message, icon: mapped.icon });
    return { isConfirmed: true };
  }

  const opts = options || {};

  if (opts.input) {
    return notifyPrompt(opts);
  }

  if (opts.showCancelButton) {
    return notifyConfirm(opts);
  }

  // Swal loading / passive wait — non-blocking toast (Swal.close dismisses it)
  if (
    opts.showConfirmButton === false &&
    !opts.input &&
    (opts.didOpen || opts.allowOutsideClick === false)
  ) {
    const { title, message } = messageFromOptions(opts);
    toast.loading(title || message || "Please wait…");
    return { isConfirmed: true };
  }

  if (opts.timer != null && opts.timer > 0) {
    const { title, message, icon: ic } = messageFromOptions(opts);
    const line = [title, message].filter(Boolean).join(": ");
    if (ic === "success") notifySuccess(line);
    else if (ic === "error") notifyError(line);
    else notifyInfo(line);
    return { isConfirmed: true };
  }

  const ic = opts.icon || "info";
  if (ic === "success" || ic === "error") {
    const { title, message } = messageFromOptions(opts);
    if (ic === "success") {
      notifySuccess(message || title, message ? title : "");
    } else {
      notifyError(message || title, message ? title : "");
    }
    return { isConfirmed: true, value: opts.inputValue };
  }

  await notifyAlert(opts);
  return { isConfirmed: true, value: opts.inputValue };
}

export const Swal = {
  fire: swalFire,
  close: () => {
    toast.dismiss();
    appDismissDialog();
  },
  getContainer: () => null,
  showLoading: () => {},
  showValidationMessage: () => {},
};

export default Swal;
